// Which leads have been AI-enriched, persisted per account — so rows stay
// marked "Enriched" across reloads, re-running enrichment on them is blocked
// (each run costs real OpenAI/Apollo spend), and the lead page can show state.
// Keyed by the lead's client row key (keyOf).
//
// That local set is only half the truth: a row can also arrive already
// researched from the import (see isRowEnriched below), which no browser-local
// mark knows about. Read both through isRowEnriched, never the set alone.
import { scopedKey } from './userScope.js'
import { keyOf } from './companyMeta.js'

const STORAGE_BASE = 'revailosales.enriched'
const storageKey = () => scopedKey(STORAGE_BASE)

const listeners = new Set()
let cache = null
let cacheKey = null

function load() {
  const key = storageKey()
  if (cache && cacheKey === key) return cache
  cacheKey = key
  try {
    cache = new Set(JSON.parse(localStorage.getItem(key) || '[]'))
  } catch {
    cache = new Set()
  }
  return cache
}

export function isEnriched(rowKey) {
  return load().has(rowKey)
}

export function enrichedSet() {
  return new Set(load())
}

// ── row-carried marks ───────────────────────────────────────────────────────
// Enrichment doesn't only happen in this browser. Imported rows can arrive
// already researched — DynamoDB's `Enriched` attribute, lowercased to
// `enriched` by normalizeApiRow — carrying a score, sales angles, objections
// and named owners/estimators. Those leads ARE enriched however empty this
// account's localStorage is, so accept the JSON boolean and the string/number
// forms a CSV or DynamoDB round-trip leaves behind.
const TRUTHY = new Set(['true', 'yes', 'y', '1'])

export function rowEnrichedFlag(row) {
  const v = row?.enriched
  if (v == null || v === '') return false
  if (typeof v === 'boolean') return v
  return TRUTHY.has(String(v).trim().toLowerCase())
}

// "Has this lead been researched?" — the row's own flag OR a mark this account
// made locally. `localSet` is the subscribed snapshot a component already holds
// (enrichedSet()), passed in so React repaints when a batch finishes; omit it
// and the persisted set is read live.
export function isRowEnriched(row, localSet) {
  if (!row) return false
  if (rowEnrichedFlag(row)) return true
  const key = keyOf(row)
  if (!key) return false
  const hit = (k) => (localSet ? localSet.has(k) : isEnriched(k))
  if (hit(key)) return true
  // A company that arrived as several region rows is merged into one
  // (leadsCache.js#mergeDuplicateCompanies), and research bought under one of
  // the ids that lost the merge is still research this workspace paid for.
  // Without this the survivor reads "not enriched" and the next run buys the
  // same dossier again.
  return (row._dupKeys || []).some(hit)
}

// Idempotent; notifies subscribers only when something new was added.
export function markEnriched(rowKeys) {
  const keys = Array.isArray(rowKeys) ? rowKeys : [rowKeys]
  const set = load()
  let added = false
  for (const k of keys) {
    if (!k || set.has(k)) continue
    set.add(k)
    added = true
  }
  if (added) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify([...set]))
    } catch {
      /* ignore */
    }
    listeners.forEach((fn) => fn())
  }
  return added
}

export function subscribeEnriched(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// ── in-flight tracking ──────────────────────────────────────────────────────
// Which rows an enrichment batch is currently working on. Not persisted: a
// reload means we are no longer the ones polling, and claiming "Enriching…"
// for a batch we lost track of would be a lie. Same subscriber list as the
// enriched marks, so a row repaints on either change.
let running = new Set()

export function enrichingSet() {
  return new Set(running)
}

export function isEnriching(rowKey) {
  return running.has(rowKey)
}

export function setEnriching(rowKeys) {
  running = new Set((rowKeys || []).filter(Boolean))
  listeners.forEach((fn) => fn())
}

export function clearEnriching(rowKeys) {
  if (!rowKeys) {
    if (!running.size) return
    running = new Set()
  } else {
    let changed = false
    for (const k of rowKeys) {
      if (running.delete(k)) changed = true
    }
    if (!changed) return
    running = new Set(running)
  }
  listeners.forEach((fn) => fn())
}
